import { ChipProps } from '@mui/material';
import React from 'react';

import { ConfidenceScoreOperator } from 'generated/graphql';

export type ChipDisplayProperties = {
  label: string;
  color: ChipProps['color'];
  style: React.CSSProperties;
};

export const NOT_ELIGIBLE: ChipDisplayProperties = {
  label: 'Not Eligible',
  color: 'error',
  style: { backgroundColor: '#F9E6E6', color: '#D32F2F' },
};

export type ConfidenceCategory = ChipDisplayProperties & {
  // The minimum value for this category (exclusive, e.g., score > min)
  min: number;
  // The maximum value for this category (inclusive, e.g., score <= max)
  max: number;
};

export const CONFIDENCE_CATEGORIES: ConfidenceCategory[] = [
  {
    label: 'No confidence',
    min: 0.0,
    max: 0.2,
    color: 'error',
    style: { backgroundColor: '#F9E6E6', color: '#D32F2F' },
  },
  {
    label: 'Low',
    min: 0.2,
    max: 0.4,
    color: 'error',
    style: { backgroundColor: '#FDF7F7', color: '#D32F2F' },
  },
  {
    label: 'Moderate',
    min: 0.4,
    max: 0.6,
    color: 'warning',
    style: { backgroundColor: '#FCEDE0', color: '#BE5602' },
  },
  {
    label: 'High',
    min: 0.6,
    max: 0.8,
    color: 'success',
    style: { backgroundColor: '#E6EFE6', color: '#235E26' },
  },
  {
    label: 'Certain',
    min: 0.8,
    max: 1.0,
    color: 'success',
    style: { backgroundColor: '#F7FAF7', color: '#2E7D32' },
  },
];

export const getConfidenceLevel = (score: number): ChipDisplayProperties => {
  const category = CONFIDENCE_CATEGORIES.find((category) => score <= category.max);
  const props = category || CONFIDENCE_CATEGORIES[CONFIDENCE_CATEGORIES.length - 1];
  const { label, color, style } = props;

  return { label, color, style };
};

/**
 * Finds a confidence category by its `max` value
 * @param max The max value (e.g., 0.6)
 * @returns The matching `ConfidenceCategory` or `undefined`.
 */
export const findCategoryByMax = (max: number): ConfidenceCategory | undefined =>
  CONFIDENCE_CATEGORIES.find((category) => category.max === max);

/**
 * Finds a category based on the *stored value* and the *operator*
 * @param value The stored form value (e.g., 0.4)
 * @param operator The operator (e.g., 'MoreOrEqual')
 * @returns The matching `ConfidenceCategory` or `undefined`.
 */
export const findCategoryByValueAndOperator = (
  value: number | undefined,
  operator: ConfidenceScoreOperator
): ConfidenceCategory | undefined => {
  if (value === undefined) return undefined;

  if (operator === ConfidenceScoreOperator.MoreOrEqual) {
    // If operator is '>=', the stored value must be a 'min'
    return CONFIDENCE_CATEGORIES.find((category) => category.min === value);
  } else {
    // If operator is '<=', the stored value must be a 'max'
    return CONFIDENCE_CATEGORIES.find((category) => category.max === value);
  }
};
